/**
 * The founder's exit thesis — founder inputs, and nothing else.
 *
 * WHAT THIS FORM CANNOT CAPTURE, by construction: an assessment, a fit ranking,
 * a probability, a computed comparison, or a verified status. Those are the
 * server's outputs; a form that could submit them would let a founder author the
 * independent assessment of their own thesis.
 *
 * SAVING IS NOT PRIVATE, and the form says so before the button rather than
 * after. A new version is visible to permitted investors, and it marks the
 * current assessment stale. Presenting this as a private note would be the more
 * comfortable copy and the wrong one — a founder should know their investors can
 * read what they are typing.
 *
 * It does NOT auto-generate. Refreshing the assessment costs one of the monthly
 * runs, and spending it because someone fixed a typo is not a decision the form
 * gets to make.
 */

import React, { useState } from 'react';
import { Text, View } from 'react-native';
import { Check } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { FormHeader, FormSection, TextField } from './AnalysisFormPrimitives';
import { parseAmount } from '@/lib/strategic-analysis/market-calculations';
import type { ExitPathwayName, ExitThesis } from '@/lib/strategic-analysis/types';
import { PATHWAY_LABELS, PATHWAY_ORDER } from './exit-labels';

const YEAR = /^\d{4}$/;
const URL_LIKE = /^https?:\/\/\S+\.\S+/i;

interface ExitThesisEditorProps {
  thesis: ExitThesis | null;
  canEdit: boolean;
  isSaving?: boolean;
  onSave?: (thesis: ExitThesis) => void;
  onCancel?: () => void;
}

export function ExitThesisEditor({
  thesis,
  canEdit,
  isSaving = false,
  onSave,
  onCancel,
}: ExitThesisEditorProps) {
  const colors = useThemeColors();

  const [preferredPathways, setPreferredPathways] = useState<ExitPathwayName[]>(
    thesis?.preferredPathways ?? [],
  );
  const [openToAssessment, setOpenToAssessment] = useState(thesis?.openToAssessment ?? false);
  const [earliestYear, setEarliestYear] = useState(
    thesis?.horizonEarliestYear ? String(thesis.horizonEarliestYear) : '',
  );
  const [latestYear, setLatestYear] = useState(
    thesis?.horizonLatestYear ? String(thesis.horizonLatestYear) : '',
  );
  const [valuationLow, setValuationLow] = useState(
    thesis?.targetValuationLow !== undefined ? String(thesis.targetValuationLow) : '',
  );
  const [valuationHigh, setValuationHigh] = useState(
    thesis?.targetValuationHigh !== undefined ? String(thesis.targetValuationHigh) : '',
  );
  const [currency, setCurrency] = useState(thesis?.targetValuationCurrency ?? 'CHF');
  const [buyerCharacteristics, setBuyerCharacteristics] = useState(
    (thesis?.buyerCharacteristics ?? []).join('\n'),
  );
  const [namedCandidates, setNamedCandidates] = useState(
    (thesis?.namedCandidates ?? []).map((c) => c.name).join('\n'),
  );
  const [rationale, setRationale] = useState(thesis?.rationale ?? '');
  const [assumptions, setAssumptions] = useState((thesis?.assumptions ?? []).join('\n'));
  const [sourceUrls, setSourceUrls] = useState((thesis?.founderSourceUrls ?? []).join('\n'));

  const lines = (raw: string) => raw.split('\n').map((l) => l.trim()).filter(Boolean);

  const togglePathway = (pathway: ExitPathwayName) => {
    setPreferredPathways((current) =>
      current.includes(pathway) ? current.filter((p) => p !== pathway) : [...current, pathway],
    );
  };

  // Mirrors the server's rules, so a submit that the server would refuse is
  // caught here with a reason instead of a generic failure.
  const problems: string[] = [];
  if (preferredPathways.length === 0 && !openToAssessment) {
    problems.push('Choose at least one preferred pathway, or say you are open to assessment.');
  }
  for (const [label, value] of [['Earliest year', earliestYear], ['Latest year', latestYear]] as const) {
    if (value && !YEAR.test(value)) problems.push(`${label} must be a four-digit year.`);
  }
  if (earliestYear && latestYear && YEAR.test(earliestYear) && YEAR.test(latestYear)) {
    if (Number(earliestYear) > Number(latestYear)) {
      problems.push('The earliest year cannot be after the latest year.');
    }
  }
  const low = parseAmount(valuationLow);
  const high = parseAmount(valuationHigh);
  // The currency field is PRE-FILLED, so it cannot be what makes a valuation
  // "started" — otherwise a founder who never enters an amount is blocked by a
  // rule meant to catch a half-entered one. A valuation begins when a bound is
  // typed; then all three parts are required, matching the server's rule.
  const valuationStarted = valuationLow.trim().length > 0 || valuationHigh.trim().length > 0;
  const valuationParts = [valuationLow, valuationHigh, currency].filter((v) => v.trim().length > 0).length;
  if (valuationStarted && valuationParts < 3) {
    problems.push('A target valuation needs a low bound, a high bound and a currency.');
  }
  if (low !== null && high !== null && low > high) {
    problems.push('The valuation low bound cannot be above the high bound.');
  }
  for (const url of lines(sourceUrls)) {
    if (!URL_LIKE.test(url)) problems.push(`“${url}” is not a valid link.`);
  }

  const canSave = canEdit && !isSaving && problems.length === 0;

  const handleSave = () => {
    if (!onSave) return;
    onSave({
      preferredPathways,
      openToAssessment,
      horizonEarliestYear: YEAR.test(earliestYear) ? Number(earliestYear) : undefined,
      horizonLatestYear: YEAR.test(latestYear) ? Number(latestYear) : undefined,
      targetValuationLow: valuationStarted && valuationParts === 3 && low !== null ? low : undefined,
      targetValuationHigh: valuationStarted && valuationParts === 3 && high !== null ? high : undefined,
      targetValuationCurrency: valuationStarted && valuationParts === 3
        ? currency.trim().toUpperCase()
        : undefined,
      buyerCharacteristics: lines(buyerCharacteristics),
      namedCandidates: lines(namedCandidates).map((name) => ({ name })),
      rationale: rationale.trim() || undefined,
      assumptions: lines(assumptions),
      founderSourceUrls: lines(sourceUrls),
      // No assessment, no fit ranking, no probability, no computed comparison,
      // no verified status. Those belong to the server.
    });
  };

  return (
    <View className="gap-4">
      <FormHeader
        title="Your exit thesis"
        description="What you intend, in your own words. Inverio assesses the evidence separately and never changes what you wrote here."
      />

      <FormSection
        title="Route and timing"
        description="Which exits you would consider, and the window you have in mind."
      >
        <Text className="text-[13px] font-semibold" style={{ color: colors.text.primary }}>
          Preferred pathways
        </Text>
        {PATHWAY_ORDER.map((pathway) => {
          const selected = preferredPathways.includes(pathway);
          return (
            <View
              key={pathway}
              className="rounded-xl overflow-hidden"
              style={{ height: 48, backgroundColor: selected ? colors.primary[50] : colors.surface.secondary }}
            >
              <AnimatedPressable
                onPress={() => togglePathway(pathway)}
                disabled={!canEdit}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: selected }}
                accessibilityLabel={PATHWAY_LABELS[pathway]}
                className="flex-row items-center gap-2.5 px-3"
                style={{ flex: 1, minHeight: 48 }}
              >
                <View
                  className="items-center justify-center rounded"
                  style={{
                    width: 20,
                    height: 20,
                    backgroundColor: selected ? colors.primary[600] : 'transparent',
                    borderWidth: selected ? 0 : 1,
                    borderColor: colors.border.medium,
                  }}
                >
                  {selected && <Check size={13} color={colors.text.inverse} />}
                </View>
                <Text numberOfLines={1} className="flex-1 text-sm" style={{ color: colors.text.primary }}>
                  {PATHWAY_LABELS[pathway]}
                </Text>
              </AnimatedPressable>
            </View>
          );
        })}

        <View
          className="rounded-xl overflow-hidden"
          style={{ height: 48, backgroundColor: openToAssessment ? colors.primary[50] : colors.surface.secondary }}
        >
          <AnimatedPressable
            onPress={() => setOpenToAssessment((v) => !v)}
            disabled={!canEdit}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: openToAssessment }}
            accessibilityLabel="I am open to assessment without a preferred pathway"
            className="flex-row items-center gap-2.5 px-3"
            style={{ flex: 1, minHeight: 48 }}
          >
            <View
              className="items-center justify-center rounded"
              style={{
                width: 20,
                height: 20,
                backgroundColor: openToAssessment ? colors.primary[600] : 'transparent',
                borderWidth: openToAssessment ? 0 : 1,
                borderColor: colors.border.medium,
              }}
            >
              {openToAssessment && <Check size={13} color={colors.text.inverse} />}
            </View>
            <Text numberOfLines={2} className="flex-1 text-sm" style={{ color: colors.text.primary }}>
              I am open to assessment without a preferred pathway
            </Text>
          </AnimatedPressable>
        </View>

        <View className="flex-row gap-3">
          <View className="flex-1">
            <TextField label="Earliest year" value={earliestYear} onChangeText={setEarliestYear} editable={canEdit} keyboardType="number-pad" placeholder="2028" />
          </View>
          <View className="flex-1">
            <TextField label="Latest year" value={latestYear} onChangeText={setLatestYear} editable={canEdit} keyboardType="number-pad" placeholder="2031" />
          </View>
        </View>
      </FormSection>

      <FormSection
        title="What you would want from it"
        description="A target range and the kind of buyer you have in mind. All optional — left empty they are simply not compared."
      >
        {/* The currency sits on its own row rather than as a third column: at
            88pt beside two flexible fields it was a slot, not a field, and on a
            phone the three of them crushed each other. */}
        <View className="flex-row gap-3">
          <View className="flex-1">
            <TextField label="Target valuation low" value={valuationLow} onChangeText={setValuationLow} editable={canEdit} keyboardType="decimal-pad" optional />
          </View>
          <View className="flex-1">
            <TextField label="Target valuation high" value={valuationHigh} onChangeText={setValuationHigh} editable={canEdit} keyboardType="decimal-pad" optional />
          </View>
        </View>
        <View className="flex-row gap-3">
          <View className="flex-1">
            <TextField label="Currency" hint="ISO code" value={currency} onChangeText={setCurrency} editable={canEdit} autoCapitalize="characters" placeholder="CHF" />
          </View>
          <View className="flex-1" />
        </View>

        <TextField
          label="Desired buyer characteristics"
          hint="One per line."
          value={buyerCharacteristics}
          onChangeText={setBuyerCharacteristics}
          editable={canEdit}
          multiline
        />
        <TextField
          label="Candidate acquirers"
          hint="One company per line. These are your own suggestions, not an Inverio assessment."
          value={namedCandidates}
          onChangeText={setNamedCandidates}
          editable={canEdit}
          multiline
        />
      </FormSection>

      <FormSection
        title="Why, and what it rests on"
        description="Your reasoning and the conditions behind it. This is what the independent assessment is compared against — it is never rewritten."
      >
        <TextField
          label="Rationale"
          hint="Why this is your thesis."
          value={rationale}
          onChangeText={setRationale}
          editable={canEdit}
          multiline
        />
        <TextField
          label="Assumptions"
          hint="One per line. What has to be true for this thesis to hold."
          value={assumptions}
          onChangeText={setAssumptions}
          editable={canEdit}
          multiline
        />
        <TextField
          label="Your sources"
          hint="One link per line."
          value={sourceUrls}
          onChangeText={setSourceUrls}
          editable={canEdit}
          multiline
        />
      </FormSection>

      {problems.length > 0 && canEdit && (
        <View className="rounded-xl p-3 gap-1" style={{ backgroundColor: colors.traffic.redLight }}>
          {problems.map((problem, i) => (
            <Text key={`problem-${i}`} className="text-sm leading-5" style={{ color: colors.error }}>
              {problem}
            </Text>
          ))}
        </View>
      )}

      {canEdit && onSave && (
        <View className="gap-2">
          <Text className="text-xs leading-4" style={{ color: colors.text.tertiary }}>
            Saving creates a new thesis version that permitted investors can see, and marks the
            current independent assessment as needing a refresh. It does not re-run the assessment
            and does not use one of your monthly runs.
          </Text>
          <View
            className="rounded-xl overflow-hidden"
            style={{ height: 48, backgroundColor: colors.primary[600], opacity: canSave ? 1 : 0.5 }}
          >
            <AnimatedPressable
              onPress={handleSave}
              disabled={!canSave}
              accessibilityLabel="Save exit thesis"
              className="items-center justify-center"
              style={{ flex: 1 }}
            >
              <Text numberOfLines={1} className="text-base font-semibold" style={{ color: colors.text.inverse }}>
                {isSaving ? 'Saving…' : 'Save exit thesis'}
              </Text>
            </AnimatedPressable>
          </View>
          {onCancel && (
            <Text
              accessibilityRole="button"
              onPress={onCancel}
              className="text-sm font-semibold text-center"
              style={{ color: colors.text.secondary }}
            >
              Cancel
            </Text>
          )}
        </View>
      )}
    </View>
  );
}

